import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for device fingerprinting
 */
export const useDeviceFingerprint = () => {
  const [fingerprint, setFingerprint] = useState(null);

  /**
   * Generate device fingerprint
   */
  const generateFingerprint = useCallback(async () => {
    try {
      const components = [];

      // Screen information
      components.push(`screen:${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);
      
      // Timezone
      components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
      
      // Language
      components.push(`lang:${navigator.language}`);
      
      // Platform
      components.push(`platform:${navigator.platform}`);
      
      // User agent (truncated for privacy)
      const userAgent = navigator.userAgent;
      const uaHash = await hashString(userAgent);
      components.push(`ua:${uaHash.substring(0, 8)}`);
      
      // Canvas fingerprint
      const canvasFingerprint = await getCanvasFingerprint();
      components.push(`canvas:${canvasFingerprint}`);
      
      // WebGL fingerprint
      const webglFingerprint = await getWebGLFingerprint();
      components.push(`webgl:${webglFingerprint}`);
      
      // Audio context fingerprint
      const audioFingerprint = await getAudioFingerprint();
      components.push(`audio:${audioFingerprint}`);
      
      // Hardware concurrency
      components.push(`cores:${navigator.hardwareConcurrency || 'unknown'}`);
      
      // Memory (if available)
      if (navigator.deviceMemory) {
        components.push(`memory:${navigator.deviceMemory}`);
      }
      
      // Connection type (if available)
      if (navigator.connection) {
        components.push(`connection:${navigator.connection.effectiveType || 'unknown'}`);
      }
      
      // Combine all components
      const fingerprintString = components.join('|');
      
      // Hash the final fingerprint
      const finalFingerprint = await hashString(fingerprintString);
      
      return finalFingerprint.substring(0, 32); // Use first 32 characters
      
    } catch (error) {
      console.error('Error generating device fingerprint:', error);
      return null;
    }
  }, []);

  /**
   * Hash a string using Web Crypto API
   */
  const hashString = useCallback(async (str) => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Error hashing string:', error);
      return str; // Fallback to original string
    }
  }, []);

  /**
   * Get canvas fingerprint
   */
  const getCanvasFingerprint = useCallback(async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = 200;
      canvas.height = 50;
      
      // Draw text
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.font = '11px Arial';
      ctx.fillText('Device fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.font = '18px Arial';
      ctx.fillText('Device fingerprint', 4, 35);
      
      // Add some geometric shapes
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'rgb(255,0,255)';
      ctx.beginPath();
      ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgb(0,255,255)';
      ctx.beginPath();
      ctx.arc(100, 50, 50, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgb(255,255,0)';
      ctx.beginPath();
      ctx.arc(75, 100, 50, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();
      
      return canvas.toDataURL();
    } catch (error) {
      console.error('Error generating canvas fingerprint:', error);
      return 'canvas_error';
    }
  }, []);

  /**
   * Get WebGL fingerprint
   */
  const getWebGLFingerprint = useCallback(async () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (!gl) {
        return 'webgl_not_supported';
      }
      
      const fingerprint = [
        gl.getParameter(gl.VERSION),
        gl.getParameter(gl.VENDOR),
        gl.getParameter(gl.RENDERER),
        gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
        gl.getParameter(gl.EXTENSIONS)
      ].join('|');
      
      return await hashString(fingerprint);
    } catch (error) {
      console.error('Error generating WebGL fingerprint:', error);
      return 'webgl_error';
    }
  }, [hashString]);

  /**
   * Get audio context fingerprint
   */
  const getAudioFingerprint = useCallback(async () => {
    try {
      if (!window.AudioContext && !window.webkitAudioContext) {
        return 'audio_not_supported';
      }
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const analyser = audioContext.createAnalyser();
      const gainNode = audioContext.createGain();
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      
      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start(0);
      
      const audioData = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(audioData);
      
      oscillator.stop();
      audioContext.close();
      
      return await hashString(audioData.toString());
    } catch (error) {
      console.error('Error generating audio fingerprint:', error);
      return 'audio_error';
    }
  }, [hashString]);

  /**
   * Initialize device fingerprint
   */
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await generateFingerprint();
        setFingerprint(fp);
        
        // Store in session storage for persistence
        if (fp) {
          sessionStorage.setItem('device_fingerprint', fp);
        }
      } catch (error) {
        console.error('Error initializing device fingerprint:', error);
      }
    };

    // Check if fingerprint already exists
    const existingFingerprint = sessionStorage.getItem('device_fingerprint');
    if (existingFingerprint) {
      setFingerprint(existingFingerprint);
    } else {
      initFingerprint();
    }
  }, [generateFingerprint]);

  return fingerprint;
};
